import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import {
  Plus,
  Loader2,
  AppWindow,
  Activity,
  Users,
  Clock,
  Zap,
  ArrowRight,
  Sparkles,
  Terminal,
  TrendingUp,
  Flame,
  Star,
} from 'lucide-react'
import {
  listMyApps,
  listPopularApps,
  listCategories,
  startApp,
  stopApp,
  rebuildApp,
  deleteApp,
  favoriteApp,
  unfavoriteApp,
  type App,
} from '../lib/api'
import { buildCategoryConfig, type CategoryStyle } from '../lib/categoryColors'
import { buildTemplateSplitButtonProps } from '../lib/templateGating'
import { formatRelativeTime } from '../lib/format'
import { withMountBase } from '../lib/mountBase'
import CreateAppModal from '../components/CreateAppModal'
import { usePublishApp } from '../hooks/usePublishApp'
import SplitButton from '../components/SplitButton'
import TemplateGalleryModal from '../components/TemplateGalleryModal'
import { AppContextMenu } from '../components/folders/AppContextMenu'
import { useFolders } from '../components/folders/useFolders'
import AppActions from '../components/AppActions'
import { VisitorBadge } from '../components/VisitorBadge'
import ShareModalComponent from '../components/ShareModal'
import EditAppInfoModal from '../components/EditAppInfoModal'
import AppSearchBar from '../components/home/AppSearchBar'
import { useSettings } from '../hooks/useSettings'
import { useCloneApp } from '../hooks/useCloneApp'

const statusConfig = {
  running: { color: 'bg-green-500', label: 'Running', pulse: true },
  stopped: { color: 'bg-gray-500', label: 'Stopped', pulse: false },
  starting: { color: 'bg-amber-500', label: 'Starting', pulse: true },
  error: { color: 'bg-red-500', label: 'Error', pulse: false },
}

let categoryConfig: Record<string, CategoryStyle> = {}

export default function Home() {
  const user = useUser()
  const navigate = useNavigate()
  const [apps, setApps] = useState<App[]>([])
  const [popularApps, setPopularApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [showTemplateGallery, setShowTemplateGallery] = useState(false)
  const [showShareApp, setShowShareApp] = useState<App | null>(null)
  const [editApp, setEditApp] = useState<App | null>(null)
  const { startClone, modal: cloneModal } = useCloneApp()
  const { settings } = useSettings()

  // Section visibility from settings (default all visible)
  const dash = (settings?.dashboard ?? {}) as Record<string, unknown>
  const showStats = dash.show_stats !== false
  const showRecentApps = dash.show_recent_apps !== false
  const showPopularApps = dash.show_popular_apps !== false
  const showQuickTips = dash.show_quick_tips !== false

  function updateAppCollections(matchName: string, updater: (app: App) => App | null) {
    const apply = (list: App[]) =>
      list.flatMap((app) => {
        if (app.name !== matchName) return [app]
        const updated = updater(app)
        return updated ? [updated] : []
      })
    setApps(apply)
    setPopularApps(apply)
  }

  const allHomeApps = [...apps, ...popularApps]
  const {
    folders,
    addAppToFolder,
    removeAppFromFolder,
    getAppFolderIds,
    createFolderAndAddApp,
  } = useFolders(allHomeApps, 'my-apps')

  useEffect(() => {
    loadApps()
  }, [])

  async function loadApps() {
    try {
      const [data, popular] = await Promise.all([
        listMyApps(),
        listPopularApps(),
      ])
      try {
        const cats = await listCategories()
        categoryConfig = buildCategoryConfig(cats)
      } catch {
        categoryConfig = {}
      }
      setApps(data)
      setPopularApps(popular)
    } catch (err) {
      console.error('Failed to load apps:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStart = useCallback(async (app: App) => {
    setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'starting' } : a))
    try {
      await startApp(app.name)
      await loadApps()
    } catch (err) {
      console.error('Failed to start app:', err)
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: app.status } : a))
    }
  }, [])

  const handleStop = useCallback(async (app: App) => {
    setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'stopped' } : a))
    try {
      await stopApp(app.name)
      await loadApps()
    } catch {
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: app.status } : a))
    }
  }, [])

  const handleRebuild = useCallback(async (app: App) => {
    try {
      await rebuildApp(app.name)
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, status: 'starting' } : a))
    } catch (err) {
      console.error('Failed to rebuild app:', err)
    }
  }, [])

  const handleDelete = useCallback(async (app: App) => {
    try {
      await deleteApp(app.name)
      updateAppCollections(app.name, () => null)
    } catch { /* ignore */ }
  }, [])

  const toggleFavorite = useCallback(async (app: App) => {
    const wasFavorited = app.is_favorited
    const update = (a: App) => a.name === app.name ? { ...a, is_favorited: !wasFavorited } : a
    setApps(prev => prev.map(update))
    setPopularApps(prev => prev.map(update))
    try {
      if (wasFavorited) await unfavoriteApp(app.name)
      else await favoriteApp(app.name)
    } catch {
      setApps(prev => prev.map(a => a.name === app.name ? { ...a, is_favorited: wasFavorited } : a))
      setPopularApps(prev => prev.map(a => a.name === app.name ? { ...a, is_favorited: wasFavorited } : a))
    }
  }, [])

  function getPermission(app: App): 'owner' | 'admin' | 'edit' | 'view' {
    const p = app.my_permission
    if (p === 'owner') return 'owner'
    if (p === 'admin') return 'admin'
    if (p === 'edit') return 'edit'
    return 'view'
  }

  const { startPublish: startPublishApp, modal: publishModal } = usePublishApp({
    onPublishStarted: () => loadApps(),
    onViewWorkDetails: (app) => navigate(`/apps/${app.name}`),
  })

  function getActionCallbacks(app: App) {
    const perm = getPermission(app)
    const canEdit = perm === 'owner' || perm === 'admin' || perm === 'edit'
    const isOwned = perm === 'owner'
    return {
      onStart: canEdit ? () => handleStart(app) : undefined,
      onStop: canEdit ? () => handleStop(app) : undefined,
      onRebuild: canEdit ? () => handleRebuild(app) : undefined,
      onRename: canEdit ? () => setEditApp(app) : undefined,
      onPublish: canEdit ? () => startPublishApp(app) : undefined,
      onShare: isOwned ? () => setShowShareApp(app) : undefined,
      onClone: canEdit ? () => startClone(app) : undefined,
      onDelete: isOwned ? () => handleDelete(app) : undefined,
    }
  }

  const runningApps = apps.filter(a => a.status === 'running')
  const recentApps = [...apps]
    .sort((a, b) => new Date(b.last_accessed || b.created_at).getTime() - new Date(a.last_accessed || a.created_at).getTime())
    .slice(0, 4)

  const greeting = getGreeting()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Empty state - no apps yet
  if (apps.length === 0) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{greeting}, {user.firstName || user.name}!</h1>
          <p className="text-muted-foreground">Let&apos;s build something amazing.</p>
        </div>

        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-3">Create your first app</h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Deploy web apps instantly. Just name it, write code, and it&apos;s live.
            No configuration needed.
          </p>
          <SplitButton
            label="Create App"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreate(true)}
            {...buildTemplateSplitButtonProps(user, () => setShowTemplateGallery(true))}
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="p-5 rounded-xl border border-border bg-card/50">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-blue-500" />
              </div>
              <h3 className="font-semibold mb-1">Instant Deploy</h3>
              <p className="text-sm text-muted-foreground">
                Apps go live at /a/your-app-name/ the moment you create them.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card/50">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-3">
                <Terminal className="w-5 h-5 text-green-500" />
              </div>
              <h3 className="font-semibold mb-1">Claude Code Built-in</h3>
              <p className="text-sm text-muted-foreground">
                Terminal opens with Claude ready to help you code.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-purple-500" />
              </div>
              <h3 className="font-semibold mb-1">Easy Sharing</h3>
              <p className="text-sm text-muted-foreground">
                Share with specific people or the whole platform.
              </p>
            </div>
          </div>
        </div>

        {publishModal}

        {/* Create App Modal */}
        {showCreate && (
          <CreateAppModal
            onClose={() => setShowCreate(false)}
            onCreated={(app) => {
              setShowCreate(false)
              const workspacePath = withMountBase('/workspace', window.location.pathname)
              const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
              const appUrl = `${window.location.origin}${appPath}`
              const target = `${workspacePath}?app=${encodeURIComponent(app.name)}&url=${encodeURIComponent(appUrl)}`
              window.location.href = target
            }}
          />
        )}

        {showTemplateGallery && (
          <TemplateGalleryModal onClose={() => setShowTemplateGallery(false)} />
        )}
      </div>
    )
  }

  // Main dashboard with apps
  return (
    <div>
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 dark:bg-background -mx-4 sm:-mx-8 px-4 sm:px-8 -mt-4 sm:-mt-8 py-4 mb-6 border-b border-border">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">{greeting}, {user.firstName || user.name}!</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">Here&apos;s what&apos;s happening with your apps.</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0 ml-auto">
            <AppSearchBar />
            <SplitButton
              label="New App"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => setShowCreate(true)}
              {...buildTemplateSplitButtonProps(user, () => setShowTemplateGallery(true))}
            />
          </div>
        </div>
      </div>

      {publishModal}

      {/* Create App Modal */}
      {showCreate && (
        <CreateAppModal
          onClose={() => setShowCreate(false)}
          onCreated={(app) => {
            setShowCreate(false)
            const workspacePath = withMountBase('/workspace', window.location.pathname)
            const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
            const appUrl = `${window.location.origin}${appPath}`
            const target = `${workspacePath}?app=${encodeURIComponent(app.name)}&url=${encodeURIComponent(appUrl)}`
            window.location.href = target
          }}
        />
      )}

      {showTemplateGallery && (
        <TemplateGalleryModal onClose={() => setShowTemplateGallery(false)} />
      )}

      {/* Share Modal */}
      {showShareApp && (
        <ShareModalComponent
          app={showShareApp}
          onClose={() => setShowShareApp(null)}
          onShared={loadApps}
        />
      )}

      {/* Edit App Info Modal */}
      {editApp && (
        <EditAppInfoModal
          app={editApp}
          onClose={() => setEditApp(null)}
          onSaved={(updated) => {
            updateAppCollections(editApp.name, (app) => ({ ...app, ...updated }))
            setEditApp(null)
          }}
        />
      )}

      {cloneModal}

      {/* Stats */}
      {showStats && <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <AppWindow className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{apps.length}</p>
              <p className="text-xs text-muted-foreground">Total Apps</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{runningApps.length}</p>
              <p className="text-xs text-muted-foreground">Running</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{apps.filter(a => a.access_level !== 'personal').length}</p>
              <p className="text-xs text-muted-foreground">Shared</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{getRecentlyActiveCount(apps)}</p>
              <p className="text-xs text-muted-foreground">Active Today</p>
            </div>
          </div>
        </div>
      </div>}

      {/* Recent Apps */}
      {showRecentApps && <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Apps</h2>
          <Link to="/apps" className="text-sm text-primary hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recentApps.map((app) => {
            const status = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
            const catCfg = app.category ? categoryConfig[app.category] : null
            const perm = getPermission(app)
            const actions = getActionCallbacks(app)

            return (
              <AppContextMenu
                key={app.id}
                app={app}
                folders={folders}
                appFolderIds={getAppFolderIds(app.name)}
                permission={perm}
                isPublishing={app.production_status === 'building'}
                onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                {...actions}
              >
                <div
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, [role="menu"]')) return
                    if (app.status === 'running' && app.url) {
                      window.open(app.url, '_blank', 'noopener,noreferrer')
                    } else {
                      navigate(`/apps/${app.name}`)
                    }
                  }}
                  className="p-4 rounded-xl border border-border bg-card hover:border-border/80 transition-colors block cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative shrink-0">
                        <div className={`w-2.5 h-2.5 rounded-full ${status.color}`} />
                        {status.pulse && (
                          <div className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${status.color} animate-ping`} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium truncate">{app.display_name || app.name}</h3>
                        <p className="text-xs text-muted-foreground">by {app.owner_name || app.owner_email || 'you'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(app) }}
                        className={`p-1.5 rounded transition-colors ${
                          app.is_favorited
                            ? 'text-yellow-500 hover:text-yellow-600'
                            : 'text-muted-foreground/40 hover:text-yellow-500'
                        }`}
                        title={app.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star className="w-4 h-4" fill={app.is_favorited ? 'currentColor' : 'none'} />
                      </button>
                      <AppActions
                        app={app}
                        permission={perm}
                        folders={folders}
                        appFolderIds={getAppFolderIds(app.name)}
                        isPublishing={app.production_status === 'building'}
                        onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                        onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                        onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                        size="lg"
                        {...actions}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    {app.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{app.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-3 pt-2 border-t border-border">
                    <VisitorBadge count={app.visit_count_30d} />
                    {catCfg && (
                      <span className={`text-[11px] ${catCfg.text}`}>#{catCfg.label}</span>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Updated {formatRelativeTime(app.updated_at)}
                    </p>
                  </div>
                </div>
              </AppContextMenu>
            )
          })}
        </div>
      </div>}

      {/* Popular Apps */}
      {showPopularApps && popularApps.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Popular Apps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {popularApps.map((app) => {
              const status = statusConfig[app.status as keyof typeof statusConfig] || statusConfig.stopped
              const catCfg = app.category ? categoryConfig[app.category] : null
              const isPopular = (app.unique_visitors_30d || 0) >= 3
              const perm = getPermission(app)
              const actions = getActionCallbacks(app)
              return (
                <AppContextMenu
                  key={app.id}
                  app={app}
                  folders={folders}
                  appFolderIds={getAppFolderIds(app.name)}
                  permission={perm}
                  isPublishing={app.production_status === 'building'}
                  onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                  onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                  onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                  {...actions}
                >
                  <div
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, [role="menu"]')) return
                      if (app.status === 'running' && app.url) {
                        window.open(app.url, '_blank', 'noopener,noreferrer')
                      } else {
                        navigate(`/apps/${app.name}`)
                      }
                    }}
                    className="p-4 rounded-xl border border-border bg-card hover:border-border/80 transition-colors block cursor-pointer h-full flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="relative shrink-0">
                          <div className={`w-2.5 h-2.5 rounded-full ${status.color}`} />
                          {status.pulse && (
                            <div className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${status.color} animate-ping`} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-medium truncate">{app.display_name || app.name}</h3>
                            {isPopular && (
                              <span className="text-orange-500" title="Popular">
                                <Flame className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">by {app.owner_name || app.owner_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(app) }}
                          className={`p-1.5 rounded transition-colors ${
                            app.is_favorited
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-muted-foreground/40 hover:text-yellow-500'
                          }`}
                          title={app.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star className="w-4 h-4" fill={app.is_favorited ? 'currentColor' : 'none'} />
                        </button>
                        <AppActions
                          app={app}
                          permission={perm}
                          folders={folders}
                          appFolderIds={getAppFolderIds(app.name)}
                          isPublishing={app.production_status === 'building'}
                          onMoveToFolder={(fid) => addAppToFolder(fid, app.name)}
                          onRemoveFromFolder={(fid) => removeAppFromFolder(fid, app.name)}
                          onCreateFolderAndMove={(name) => createFolderAndAddApp(name, app.name)}
                          size="lg"
                          {...actions}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      {app.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{app.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-border">
                      <VisitorBadge count={app.visit_count_30d} />
                      {catCfg && (
                        <span className={`text-[11px] ${catCfg.text}`}>#{catCfg.label}</span>
                      )}
                    </div>
                  </div>
                </AppContextMenu>
              )
            })}
          </div>
        </div>
      )}

      {/* Quick Tips */}
      {showQuickTips && (
        <div className="p-5 rounded-xl border border-blue-100 dark:border-blue-900/30 bg-blue-50 dark:bg-blue-950/20">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-500" />
            Quick Tips
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
            <div>
              <span className="text-foreground font-medium">Claude Code:</span> Terminal auto-starts{' '}
              <code className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-xs">claude</code> for you
            </div>
            <div>
              <span className="text-foreground font-medium">Auto-detect:</span> Runtime detected from package.json, requirements.txt, or index.html
            </div>
            <div>
              <span className="text-foreground font-medium">Instant URLs:</span> Apps live at{' '}
              <code className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-xs">/a/app-name/</code>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function getRecentlyActiveCount(apps: App[]): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return apps.filter(app => {
    const lastAccessed = app.last_accessed ? new Date(app.last_accessed) : null
    return lastAccessed && lastAccessed >= today
  }).length
}
